import {Extension, Mark, mergeAttributes} from "@tiptap/react";
import {Plugin, PluginKey} from 'prosemirror-state';

export const PreserveMarks = Extension.create({
    name: "preserveMarks",
    addProseMirrorPlugins() {
        return [
            new Plugin({
                appendTransaction: (transactions, oldState, newState) => {
                    const docChanged = transactions.some(tr => tr.docChanged)
                    if (!docChanged) return

                    const marks = oldState.storedMarks || oldState.selection.$from.marks();
                    if (!marks?.length) return

                    const {tr} = newState

                    marks.forEach(mark => {
                        tr.addStoredMark(mark)
                    })

                    return tr
                },
            }),
        ]
    },
})

export const DiffAdded = Mark.create({
    name: "diffAdded",
    parseHTML() {
        return [{tag: "ins"}]
    },
    renderHTML() {
        return ["ins", {class: "diff-added"}, 0]
    },
})

export const DiffRemoved = Mark.create({
    name: "diffRemoved",
    parseHTML() {
        return [{tag: "del"}]
    },
    renderHTML() {
        return ["del", {class: "diff-removed"}, 0]
    },
})

export const DiffModified = Mark.create({
    name: "diffModified",
    parseHTML() {
        return [{tag: "mod"}]
    },
    renderHTML() {
        return ["mod", {class: "diff-modified"}, 0]
    },
})

export const CommentMark = Mark.create({
    name: "comment",
    inclusive: true,
    excludes: "",
    addAttributes() {
        return {
            commentId: {
                default: null,
                parseHTML: el => el.getAttribute("commentid"),
                renderHTML: attributes => ({commentid: attributes.commentId}),
            },
            commentState: {
                default: null,
                parseHTML: el => el.getAttribute("comment-state"),
                renderHTML: attributes => ({
                    "comment-state": attributes.commentState,
                    class: attributes.commentState ? `comment-${attributes.commentState}` : undefined,
                }),
            }
        }
    },
    parseHTML() {
        return [{tag: "span[commentid]"}]
    },
    renderHTML({HTMLAttributes}) {
        return ["span", mergeAttributes(HTMLAttributes), 0]
    },
    addProseMirrorPlugins() {
        const markType = this.type;
        const {onExitComment} = this.options;
        const editor = this.editor;

        return [
            new Plugin({
                filterTransaction(tr, state) {
                    if (tr.getMeta("skipCommentFilter")) return true;
                    if (!tr.docChanged) return true;
                    const markType = state.schema.marks.comment;
                    const ranges = new Map();

                    tr.doc.descendants((node) => {
                        if (!node.isText) return;

                        node.marks.forEach(mark => {
                            if (mark.type !== markType) return;

                            const id = mark.attrs.commentId;
                            ranges.set(id, (ranges.get(id) || 0) + node.nodeSize);
                        });
                    });

                    const prevRanges = new Map();

                    state.doc.descendants((node) => {
                        if (!node.isText) return;

                        node.marks.forEach(mark => {
                            if (mark.type !== markType) return;

                            const id = mark.attrs.commentId;
                            prevRanges.set(id, (prevRanges.get(id) || 0) + node.nodeSize);
                        });
                    });

                    for (const [id, size] of prevRanges.entries()) {
                        const newSize = ranges.get(id) || 0;

                        if (newSize <= 0) {
                            return false;
                        }
                    }

                    return true;
                },
                props: {
                    handleKeyDown(view, event) {
                        if (event.key !== " " && event.key !== "Enter") return false;

                        const {state, dispatch} = view;
                        const {selection} = state;
                        const {$from} = selection;

                        const hasCommentMark = $from.marks().some(m => m.type === markType);
                        if (!hasCommentMark) return false;

                        const index = $from.parentOffset;
                        const parentText = $from.parent.textContent;
                        const charBefore = parentText[index - 1];

                        if (charBefore !== " " && charBefore !== "\u00A0") return false;

                        const pos = selection.from;

                        const tr = state.tr
                            .removeMark(pos - 1, pos, markType)
                            .removeStoredMark(markType)
                            .setMeta("exitComment", true);

                        dispatch(tr);
                        onExitComment(editor);

                        return event.key !== "Enter";
                    },
                },
            }),
        ];
    }
})

const hasDiffRemovedMark = (state) => {
    const {from} = state.selection;
    const marks = state.doc.resolve(from).marks();
    return marks.some(m => m.type.name === "diffRemoved");
}

const isInDiffRemoved = (view) => {
    const {$from} = view.state.selection;
    const node = $from.node();
    const cls = node.attrs?.class ?? "";
    const blockNode = cls.includes("diff-removed") || cls.includes("diff-empty-removed");
    const blockMark = hasDiffRemovedMark(view.state);
    return blockNode || blockMark;
}

export const BlockDiffRemoved = Extension.create({
    name: "blockDiffRemoved",
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey("blockDiffRemoved"),
                props: {
                    handleKeyDown(view, event) {
                        if (!isInDiffRemoved(view)) return false;

                        if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) return false;

                        if (event.key === "Enter") {
                            const {state, dispatch} = view;
                            const {tr, schema} = state;
                            const {$from} = state.selection;
                            const end = $from.end($from.depth);

                            const emptyParagraph = schema.nodes.paragraph.create({
                                id: crypto.randomUUID(),
                                class: null,
                                textAlign: "left",
                                lineHeight: null,
                                marginLeft: 0
                            })

                            tr.insert(end + 1, emptyParagraph);
                            tr.setSelection(state.selection.constructor.near(tr.doc.resolve(end + 2)));
                            dispatch(tr);
                            return true;
                        }

                        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
                            const {state, dispatch} = view;
                            const {tr} = state;
                            const {$from} = state.selection;
                            const end = $from.end($from.depth);

                            tr.setSelection(state.selection.constructor.near(tr.doc.resolve(end + 1)));
                            dispatch(tr);
                            return false;
                        }

                        return true;
                    },
                    handlePaste(view) {
                        if (!isInDiffRemoved(view)) return false;

                        const {state, dispatch} = view;
                        const {tr} = state;
                        const {$from} = state.selection;
                        const end = $from.end($from.depth);

                        tr.setSelection(state.selection.constructor.near(tr.doc.resolve(end + 1)));
                        dispatch(tr);
                        return false;
                    },
                },
            }),
        ]
    },
})